<script setup lang="ts">
import BundleInfo from './BundleInfo.vue';
import HotelCard from '@components/util/hotel-card';
import type BundlePackage from '@itinerary/bundle/BundlePackage';

interface IProps {
    bundlePackage: BundlePackage;
    mobileView?: boolean;
    loading?: boolean;
    mobileObserving?: boolean;
}

const { loading, bundlePackage, mobileView } = defineProps<IProps>();
const emit = defineEmits<{
    reserve: [];
}>();
const hotelPackage = computed(() => bundlePackage?.hotelPackage || null);
const bundleTitle = computed(
    () =>
        `${hotelPackage.value?.nights} شب ${bundlePackage.roomsCount} اتاق + هواپیما`
);
const totalPrice = computed(() => bundlePackage.singlePrice);
const payable = computed(() => bundlePackage.singlePayable);
const discountedPrice = computed(() =>
    (payable.value || 0) < (totalPrice.value || 0)
        ? totalPrice.value || 0
        : null
);
const percent = computed(() =>
    Math.ceil(100 - ((payable.value || 0) / (totalPrice.value || 0)) * 100)
);
const bundlePaxCountTitle = computed(
    () => `${bundlePackage.totalPaxCounts}مسافر`
);
const i18n = useI18n();
</script>

<template>
    <HotelCard
        :hotel-item="hotelPackage || undefined"
        :mobile-view
        :mobile-observing
        :class="{
            'mobile-view': mobileView,
        }"
        disable-title-link
        @click="emit('reserve')"
    >
        <template #extra-info>
            <mr-divider class="info-divider" />
            <BundleInfo
                class="desktop-bundle-info"
                :bundle-pax-count-title
                :bundle-title
            />
        </template>
        <template #pricing>
            <BundleInfo
                class="mobile-bundle-info"
                :bundle-pax-count-title
                :bundle-title
                mobile-view
            />
            <!-- not tested -->
            <div class="price-view-container">
                <mr-price-re
                    v-if="discountedPrice"
                    class="off-price-container"
                    :price="discountedPrice"
                    :percent
                    type="discount"
                >
                    <template #prefix>
                        <div class="bundle-discount-title">
                            تخفیف {{ bundleTitle }}
                        </div>
                    </template>
                </mr-price-re>
                <mr-price-re
                    class="price"
                    :class="{ 'loading-skeleton': loading }"
                    :price="payable"
                    prefix="هرنفر"
                />
                <mr-price-re
                    class="price"
                    :class="{ 'loading-skeleton': loading }"
                    :price="bundlePackage.sumTotalPrice"
                    prefix="مجموع"
                    type="small"
                />
            </div>
            <mr-button
                class="reserve-button"
                full-width
                @click="emit('reserve')"
            >
                {{ i18n.t('HotelCard.selectRoom') }}
            </mr-button>
        </template>
    </HotelCard>
</template>

<style lang="scss" scoped>
.mobile-bundle-info {
    display: none;
}

.reserve-button {
    margin-top: auto;
    @include mobile-only {
        display: none;
    }
}

.price-view-container {
    display: flex;
    flex-direction: column;
    .bundle-discount-title {
        @include tg-s($w9, usePalette($p-green, $p-500));
    }
}

.info-divider {
    margin: auto 0 8px 0;
}

@mixin mobileStyle {
    .info-divider {
        display: none;
    }
    .desktop-bundle-info {
        display: none;
    }
    .mobile-bundle-info {
        display: flex;
    }
}

.mobile-view {
    @include mobileStyle();
}

@media (max-width: $app-mobile-max-width) {
    @include mobileStyle();
}
</style>
